/**
 * Skin thật (s8, docs/asset-guide.md): manifest + atlas của `packages/assets/skins/pixel-default`.
 * Game chỉ gọi logical key; tải hỏng hoặc thiếu key thì trả null → nơi gọi vẽ placeholder.
 * Ảnh atlas đi qua pipeline của Vite (tên file có hash) nên được service worker precache.
 */
import { Assets, Rectangle, Texture } from 'pixi.js';
import {
  parseAtlas,
  parseManifest,
  resolveFrame,
  type AtlasData,
  type ManifestEntry,
  type SkinManifest,
} from '@ml/assets';
import manifestJson from '../../../../packages/assets/skins/pixel-default/manifest.json';
import atlasJson from '../../../../packages/assets/skins/pixel-default/atlas.json';
import atlasUrl from '../../../../packages/assets/skins/pixel-default/atlas.png?url';

export { atlasUrl };

/** Key cũ của orbArt (s9) → key trong manifest. */
const ALIAS: Record<string, string> = {
  'orb.lightball': 'booster.lightball',
  'orb.essence': 'essence.t1',
  'overlay.freeze.1': 'overlay.freeze1',
  'overlay.freeze.2': 'overlay.freeze2',
  'overlay.freeze.3': 'overlay.freeze3',
};

export class Skin {
  private readonly cache = new Map<string, Texture>();

  constructor(
    readonly manifest: SkinManifest,
    readonly atlas: AtlasData,
    private readonly base: Texture,
  ) {}

  entry(key: string): ManifestEntry | null {
    return this.manifest.entries[ALIAS[key] ?? key] ?? null;
  }

  has(key: string): boolean {
    return this.entry(key) !== null;
  }

  /** Khung (px atlas) của frame `i` của key, null nếu thiếu. */
  rect(key: string, i = 0): { x: number; y: number; w: number; h: number } | null {
    const k = ALIAS[key] ?? key;
    if (!this.manifest.entries[k]) return null;
    return resolveFrame(this.manifest, { main: this.atlas }, k, i).rect;
  }

  texture(key: string, i = 0): Texture | null {
    const k = ALIAS[key] ?? key;
    if (!this.manifest.entries[k]) return null;
    const { frame, rect } = resolveFrame(this.manifest, { main: this.atlas }, k, i);
    let t = this.cache.get(frame);
    if (!t) {
      t = new Texture({
        source: this.base.source,
        frame: new Rectangle(rect.x, rect.y, rect.w, rect.h),
      });
      this.cache.set(frame, t);
    }
    return t;
  }

  /** Mọi frame của một anim (1 phần tử nếu là frame tĩnh). */
  frames(key: string): Texture[] {
    const e = this.entry(key);
    if (!e) return [];
    const n = e.type === 'anim' ? e.frames.length : 1;
    return Array.from({ length: n }, (_, i) => this.texture(key, i)!);
  }

  fps(key: string): number {
    const e = this.entry(key);
    return e && e.type === 'anim' ? e.fps : 6;
  }

  loops(key: string): boolean {
    const e = this.entry(key);
    return e && e.type === 'anim' ? e.loop : true;
  }

  anchor(key: string): [number, number] {
    const e = this.entry(key);
    return e ? [e.anchor[0], e.anchor[1]] : [0.5, 0.5];
  }
}

/** Tải skin mặc định. Lỗi (mạng, schema) → null, game chạy bằng placeholder. */
export async function loadSkin(): Promise<Skin | null> {
  try {
    const manifest = parseManifest(manifestJson);
    const atlas = parseAtlas(atlasJson);
    const base = await Assets.load<Texture>(atlasUrl);
    base.source.scaleMode = 'nearest';
    return new Skin(manifest, atlas, base);
  } catch (e) {
    console.warn('[skin] tải skin hỏng, dùng placeholder:', e);
    return null;
  }
}
